<template>
  <div 
    class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-gray-900/60 backdrop-blur-sm"
    @click.self="closeModal"
  >
    <div class="bg-white rounded-2xl shadow-2xl max-w-5xl w-full overflow-hidden flex flex-col max-h-[90vh] animate-in fade-in zoom-in-95 duration-200">
      <!-- Header del Modal -->
      <div class="px-6 py-4 border-b border-gray-100 flex items-center justify-between bg-gray-50/50">
        <div class="flex items-center gap-3">
          <div class="w-10 h-10 rounded-xl bg-orange-100 text-orange-600 flex items-center justify-center text-lg">
            <i class="fa fa-file-invoice" />
          </div>
          <div>
            <h3 class="text-base sm:text-lg font-bold text-gray-900">Importación Masiva de Ventas</h3>
            <p class="text-xs text-gray-500">
              {{ importStep === 'upload' ? 'Paso 1: Descarga la plantilla y sube tu archivo' : 'Paso 2: Previsualiza las ventas y emite la importación' }}
            </p>
          </div>
        </div>
        <button 
          class="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-400 hover:text-gray-600 flex items-center justify-center transition-colors"
          @click="closeModal"
        >
          <i class="fa fa-times text-sm" />
        </button>
      </div>

      <!-- Contenido: Paso 1 (Subida & Plantilla) -->
      <div v-if="importStep === 'upload'" class="p-6 overflow-y-auto space-y-6 flex-1">
        <!-- Banner Descargar Plantilla -->
        <div class="bg-gradient-to-r from-orange-50 to-amber-50 border border-orange-200/80 rounded-2xl p-5 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div class="flex items-center gap-4">
            <div class="w-12 h-12 rounded-xl bg-white shadow-sm border border-orange-100 flex items-center justify-center shrink-0">
              <i class="fa fa-file-excel text-green-600 text-2xl" />
            </div>
            <div>
              <h4 class="text-sm font-bold text-gray-900">Plantilla Oficial de Ventas</h4>
              <p class="text-xs text-gray-600 mt-0.5 max-w-md">
                Descarga el formato Excel listo con los encabezados requeridos (número de orden, fecha, cliente, cédula/RIF, código de producto, cantidad, precio, método de pago, estado) y datos de ejemplo.
              </p>
            </div>
          </div>
          <a 
            href="/orders/template" 
            class="px-4 py-2.5 bg-orange-500 hover:bg-orange-600 text-white rounded-xl text-xs font-bold uppercase tracking-wider flex items-center gap-2 shrink-0 transition-colors shadow-sm shadow-orange-200 active:scale-95"
          >
            <i class="fa fa-download" />
            Descargar Plantilla
          </a>
        </div>

        <!-- Drag and Drop / Selector de Archivo -->
        <div>
          <label class="block text-xs font-bold uppercase text-gray-500 tracking-wider mb-2">
            Sube tu archivo completado (.xlsx, .xls, .csv)
          </label>
          <div 
            :class="[
              'border-2 border-dashed rounded-2xl p-8 text-center cursor-pointer transition-all flex flex-col items-center justify-center gap-3',
              isDragging ? 'border-orange-500 bg-orange-50/50' : 'border-gray-200 hover:border-orange-400 hover:bg-gray-50/50 bg-white'
            ]"
            @dragover.prevent="isDragging = true"
            @dragleave.prevent="isDragging = false"
            @drop.prevent="handleFileDrop"
            @click="$refs.fileInput.click()"
          >
            <input 
              ref="fileInput" 
              type="file" 
              class="hidden" 
              accept=".xlsx,.xls,.csv" 
              @change="handleFileSelected" 
            />
            
            <div v-if="isPreviewLoading" class="flex flex-col items-center gap-2 py-4">
              <i class="fa fa-spinner fa-spin text-3xl text-orange-500" />
              <p class="text-sm font-bold text-gray-700">Analizando archivo...</p>
              <p class="text-xs text-gray-400">Leyendo y validando ventas y productos</p>
            </div>

            <template v-else>
              <div class="w-14 h-14 rounded-full bg-orange-50 text-orange-500 flex items-center justify-center text-2xl">
                <i class="fa fa-cloud-upload-alt" />
              </div>
              <div>
                <p class="text-sm font-bold text-gray-800">
                  Haz clic aquí para seleccionar tu archivo o arrástralo hasta esta zona
                </p>
                <p class="text-xs text-gray-400 mt-1">
                  Formatos compatibles: Microsoft Excel (.xlsx, .xls) o CSV (.csv)
                </p>
              </div>
            </template>
          </div>
          
          <p v-if="previewError" class="text-xs text-red-600 mt-3 font-semibold flex items-center gap-1.5 bg-red-50 p-2.5 rounded-lg border border-red-100">
            <i class="fa fa-exclamation-circle" />
            {{ previewError }}
          </p>
        </div>
      </div>

      <!-- Contenido: Paso 2 (Previsualización) -->
      <div v-else-if="importStep === 'preview'" class="p-6 overflow-y-auto space-y-4 flex-1 flex flex-col">
        <!-- Resumen de detección -->
        <div class="bg-gray-50 border border-gray-200 rounded-xl p-4 flex flex-wrap items-center justify-between gap-4">
          <div class="flex items-center gap-3">
            <div class="w-9 h-9 rounded-lg bg-orange-100 text-orange-600 flex items-center justify-center text-sm font-bold">
              <i class="fa fa-table" />
            </div>
            <div>
              <div class="text-sm font-bold text-gray-900">
                Previsualización: {{ previewData.total }} ventas listas para importar
              </div>
              <div class="text-xs text-gray-500">
                Archivo cargado: <span class="font-semibold text-gray-700">{{ selectedFileName }}</span>
              </div>
            </div>
          </div>

          <div class="flex items-center gap-2">
            <span class="px-2.5 py-1 rounded-full text-xs font-bold bg-green-100 text-green-700 border border-green-200">
              <i class="fa fa-plus-circle mr-1" /> {{ previewData.newCount }} Nuevas
            </span>
            <span class="px-2.5 py-1 rounded-full text-xs font-bold bg-blue-100 text-blue-700 border border-blue-200">
              <i class="fa fa-sync-alt mr-1" /> {{ previewData.updateCount }} A actualizar
            </span>
          </div>
        </div>

        <!-- Tabla de Previsualización -->
        <div class="border border-gray-200 rounded-xl overflow-hidden flex-1 flex flex-col min-h-[260px] max-h-[380px]">
          <div class="overflow-x-auto overflow-y-auto flex-1">
            <table class="w-full text-left text-xs whitespace-nowrap">
              <thead class="bg-gray-100 text-gray-600 uppercase font-bold sticky top-0 z-10 border-b border-gray-200">
                <tr>
                  <th class="py-2.5 px-3 w-10">#</th>
                  <th class="py-2.5 px-3"># Orden</th>
                  <th class="py-2.5 px-3">Fecha</th>
                  <th class="py-2.5 px-3">Cliente</th>
                  <th class="py-2.5 px-3">Cédula / RIF</th>
                  <th class="py-2.5 px-3">Productos</th>
                  <th class="py-2.5 px-3 text-right">Total</th>
                  <th class="py-2.5 px-3">Método</th>
                  <th class="py-2.5 px-3 text-center">Estado</th>
                  <th class="py-2.5 px-3 text-center">Acción</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-gray-100">
                <tr v-for="row in previewData.rows" :key="row.index" class="hover:bg-gray-50/70">
                  <td class="py-2 px-3 text-gray-400 font-mono">{{ row.index }}</td>
                  <td class="py-2 px-3 font-mono text-gray-700 font-semibold">{{ row.number_order }}</td>
                  <td class="py-2 px-3 text-gray-600">{{ row.date }}</td>
                  <td class="py-2 px-3 font-semibold text-gray-900">{{ row.client }}</td>
                  <td class="py-2 px-3 font-mono text-gray-600">{{ row.client_id }}</td>
                  <td class="py-2 px-3 text-gray-600 max-w-xs truncate" :title="row.products_summary">{{ row.products_summary }}</td>
                  <td class="py-2 px-3 text-right font-bold text-gray-900">{{ formatCurrency(row.total) }}</td>
                  <td class="py-2 px-3 text-gray-600">{{ row.method }}</td>
                  <td class="py-2 px-3 text-center">
                    <span class="px-2 py-0.5 rounded text-[10px] font-semibold bg-gray-100 text-gray-700">
                      {{ row.state }}
                    </span>
                  </td>
                  <td class="py-2 px-3 text-center">
                    <span 
                      v-if="row.action === 'new'" 
                      class="px-2 py-0.5 rounded-full text-[10px] font-bold bg-green-50 text-green-700 border border-green-200"
                    >
                      Nueva
                    </span>
                    <span 
                      v-else 
                      class="px-2 py-0.5 rounded-full text-[10px] font-bold bg-blue-50 text-blue-700 border border-blue-200"
                    >
                      Actualizar
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <p v-if="previewError" class="text-xs text-red-600 font-semibold flex items-center gap-1.5 bg-red-50 p-2.5 rounded-lg border border-red-100">
          <i class="fa fa-exclamation-circle" />
          {{ previewError }}
        </p>
      </div>

      <!-- Footer del Modal -->
      <div class="px-6 py-4 bg-gray-50 border-t border-gray-100 flex items-center justify-between">
        <button 
          type="button"
          class="px-4 py-2 border border-gray-300 hover:bg-gray-100 text-gray-700 rounded-xl text-xs font-bold uppercase tracking-wider transition-colors"
          @click="closeModal"
        >
          Cancelar
        </button>

        <div class="flex items-center gap-3">
          <button 
            v-if="importStep === 'preview'"
            type="button"
            :disabled="isImporting"
            class="px-4 py-2 bg-white border border-gray-300 hover:bg-gray-100 text-gray-700 rounded-xl text-xs font-bold uppercase tracking-wider transition-colors disabled:opacity-50"
            @click="resetImportFile"
          >
            <i class="fa fa-arrow-left mr-1" />
            Cambiar Archivo
          </button>

          <button 
            v-if="importStep === 'preview'"
            type="button"
            :disabled="isImporting || previewData.total === 0"
            class="px-6 py-2.5 bg-orange-600 hover:bg-orange-700 text-white rounded-xl text-xs font-black uppercase tracking-wider transition-all shadow-md shadow-orange-300 flex items-center gap-2 active:scale-95 disabled:opacity-50"
            @click="submitImport"
          >
            <i v-if="isImporting" class="fa fa-spinner fa-spin" />
            <i v-else class="fa fa-rocket" />
            <span>{{ isImporting ? 'Emitiendo Importación...' : 'Emitir Importación' }}</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import Swal from 'sweetalert2'

export default {
  name: 'OrderImportModal',

  props: {
    csrf: {
      type: String,
      default: '',
    },
  },

  emits: ['close', 'imported'],

  data() {
    return {
      importStep: 'upload', // 'upload' | 'preview'
      selectedFile: null,
      selectedFileName: '',
      isDragging: false,
      isPreviewLoading: false,
      isImporting: false,
      previewError: '',
      previewData: {
        total: 0,
        newCount: 0,
        updateCount: 0,
        rows: [],
      },
    }
  },

  methods: {
    formatCurrency(value) {
      return new Intl.NumberFormat('es-CO', {
        style: 'currency',
        currency: 'COP',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(value || 0)
    },

    closeModal() {
      if (this.isImporting) return
      this.resetImportFile()
      this.$emit('close')
    },

    resetImportFile() {
      this.importStep = 'upload'
      this.selectedFile = null
      this.selectedFileName = ''
      this.isPreviewLoading = false
      this.isImporting = false
      this.previewError = ''
      this.previewData = {
        total: 0,
        newCount: 0,
        updateCount: 0,
        rows: [],
      }
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = ''
      }
    },

    handleFileSelected(event) {
      const file = event.target.files[0]
      if (file) {
        this.processFile(file)
      }
    },

    handleFileDrop(event) {
      this.isDragging = false
      const file = event.dataTransfer.files[0]
      if (file) {
        this.processFile(file)
      }
    },

    async processFile(file) {
      this.selectedFile = file
      this.selectedFileName = file.name
      this.isPreviewLoading = true
      this.previewError = ''

      const formData = new FormData()
      formData.append('file', file)

      try {
        const response = await axios.post('/orders/import/preview', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
            'X-CSRF-TOKEN': this.csrf || document.querySelector('meta[name="csrf-token"]')?.getAttribute('content'),
          },
        })

        if (response.data.success) {
          if (response.data.total === 0) {
            this.previewError = 'El archivo está vacío o no contiene filas válidas de ventas.'
            this.isPreviewLoading = false
            return
          }
          this.previewData = response.data
          this.importStep = 'preview'
        } else {
          this.previewError = response.data.message || 'Error al procesar el archivo.'
        }
      } catch (error) {
        this.previewError = error.response?.data?.message || 'Error al conectar con el servidor para previsualizar.'
      } finally {
        this.isPreviewLoading = false
      }
    },

    async submitImport() {
      if (!this.selectedFile) return
      this.isImporting = true
      this.previewError = ''

      const formData = new FormData()
      formData.append('file', this.selectedFile)

      try {
        const response = await axios.post('/orders/import', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
            'Accept': 'application/json',
            'X-CSRF-TOKEN': this.csrf || document.querySelector('meta[name="csrf-token"]')?.getAttribute('content'),
          },
        })

        if (response.data.success) {
          this.$emit('imported', response.data)
          this.closeModal()

          const alertHelper = this.$swal || Swal
          if (alertHelper) {
            alertHelper.fire({
              title: '¡Importación Exitosa!',
              text: response.data.message || 'Las ventas han sido registradas correctamente.',
              icon: 'success',
              confirmButtonColor: '#f97316',
              confirmButtonText: 'Aceptar',
            })
          }

          this.$inertia.reload({ preserveScroll: true })
        } else {
          this.previewError = response.data.message || 'Ocurrió un error al importar las ventas.'
        }
      } catch (error) {
        this.previewError = error.response?.data?.message || 'Error inesperado al emitir la importación.'
      } finally {
        this.isImporting = false
      }
    },
  },
}
</script>
